import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { handleLogin, handleVerifyCode, handleResendCode, handlePasswordResetRequest, handlePasswordResetConfirm } from '../controllers/authController';
import { secureStorage } from '../utils/security';

const sanitize = (str) => String(str).replace(/<[^>]*>/g, '').trim().slice(0, 256);

export default function LoginModal({ onClose, onSwitchToRegister }) {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [step,    setStep]    = useState('login'); // 'login' | 'verify' | 'reset_request' | 'reset_confirm'
  const [form,    setForm]    = useState({ email: '', password: '' });
  const [userId,  setUserId]  = useState(null);
  const [code,    setCode]    = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error,   setError]   = useState('');
  const [info,    setInfo]    = useState('');
  const [resent,  setResent]  = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', h); document.body.style.overflow = ''; };
  }, [onClose]);

  const set = (f, v) => setForm(p => ({ ...p, [f]: sanitize(v) }));

  const goToDashboard = (role) => {
    if (role === 'admin')         navigate('/dashboard/admin');
    else if (role === 'supplier') navigate('/dashboard/supplier');
    else                          navigate('/dashboard/customer');
  };

  const submit = async () => {
    setError(''); setInfo('');
    if (!form.email)    return setError('E-posta adresi zorunludur.');
    if (!form.password) return setError('Şifre zorunludur.');
    setLoading(true);
    const res = await handleLogin(form, login, navigate);
    setLoading(false);
    if (res?.needsVerification) {
      // Account exists but isn't verified — switch to code entry.
      setUserId(res.user_id);
      setStep('verify');
      setInfo('E-posta adresinize gönderilen 6 haneli kodu girin.');
      return;
    }
    if (res?.error) { setError(res.error); return; }
    onClose();
  };

  const verifyCode = async () => {
    setError(''); setInfo('');
    setLoading(true);
    const result = await handleVerifyCode(userId, code);
    setLoading(false);
    if (result.error) { setError(result.error); return; }

    if (result.token && result.user) {
      // Verified and logged in (customers).
      secureStorage.setToken(result.token);
      login(result.user);
      onClose();
      goToDashboard(result.user.role);
    } else {
      // Verified but not auto-logged-in (e.g. supplier awaiting admin approval).
      setStep('login');
      setCode('');
      setInfo('E-posta adresiniz doğrulandı. Tedarikçi hesabınız admin onayı bekliyor.');
    }
  };

  const resend = async () => {
    setResent(false); setError('');
    const result = await handleResendCode(userId);
    if (result.error) setError(result.error);
    else setResent(true);
  };

  const resetRequest = async () => {
    setError(''); setInfo('');
    if (!form.email) return setError('E-posta adresi zorunludur.');
    setLoading(true);
    const res = await handlePasswordResetRequest(form.email);
    setLoading(false);
    if (res.error) { setError(res.error); return; }
    setStep('reset_confirm');
    setInfo(res.message || 'E-posta adresinize bir kod gönderildi.');
  };

  const resetConfirm = async () => {
    setError(''); setInfo('');
    setLoading(true);
    const res = await handlePasswordResetConfirm(form.email, code, newPassword);
    setLoading(false);
    if (res.error) { setError(res.error); return; }
    setStep('login');
    setCode(''); setNewPassword('');
    setInfo(res.message || 'Şifreniz güncellendi. Giriş yapabilirsiniz.');
  };

  const inpCls = 'w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-8 relative"
        onClick={e => e.stopPropagation()}>

        <button onClick={onClose}
          className="absolute top-4 right-4 text-gray-300 hover:text-gray-500 text-xl leading-none transition">
          ✕
        </button>

        <div className="text-2xl font-bold text-gray-900 mb-1">
          Oto<span className="text-blue-700">ParçamıBul</span>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-100 text-red-600 rounded-xl px-4 py-3 text-sm mb-5 mt-4">
            ⚠️ {error}
          </div>
        )}
        {info && !error && (
          <div className="bg-blue-50 border border-blue-100 text-blue-700 rounded-xl px-4 py-3 text-sm mb-5 mt-4">
            ℹ️ {info}
          </div>
        )}
        {resent && (
          <div className="bg-green-50 border border-green-100 text-green-700 rounded-xl px-4 py-3 text-sm mb-5">
            ✓ Yeni kod gönderildi.
          </div>
        )}

        {/* ── Login step ── */}
        {step === 'login' && (
          <>
            <h2 className="text-xl font-bold text-gray-800 mb-1">Hesabınıza Giriş Yapın</h2>
            <p className="text-gray-400 text-sm mb-7">Tedarikçi veya müşteri hesabınıza giriş yapın.</p>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">E-posta</label>
              <input type="email" autoComplete="email" className={inpCls}
                placeholder="email@adresiniz.com"
                value={form.email} maxLength={100}
                onChange={e => set('email', e.target.value)}
                onKeyDown={e => e.key === 'Enter' && submit()}
              />
            </div>

            <div className="mb-6">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Şifre</label>
              <input type="password" autoComplete="current-password" className={inpCls}
                placeholder="••••••••"
                value={form.password} maxLength={128}
                onChange={e => set('password', e.target.value)}
                onKeyDown={e => e.key === 'Enter' && submit()}
              />
            </div>

            <button onClick={submit} disabled={loading}
              className="w-full bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white font-semibold py-3 rounded-xl text-sm transition mb-3">
              {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
            </button>

            <div className="text-center mb-5">
              <button onClick={() => { setStep('reset_request'); setError(''); setInfo(''); }}
                className="text-xs text-gray-400 hover:text-blue-700 transition">
                Şifremi unuttum?
              </button>
            </div>

            <div className="relative text-center text-gray-300 text-sm mb-5">
              <span className="bg-white px-3 relative z-10">veya</span>
              <div className="absolute inset-x-0 top-1/2 h-px bg-gray-100 z-0" />
            </div>

            <p className="text-center text-sm text-gray-500">
              Hesabınız yok mu?{' '}
              <button onClick={() => { onClose(); onSwitchToRegister?.(); }}
                className="text-blue-700 font-semibold hover:underline">
                Ücretsiz Kayıt Ol
              </button>
            </p>
          </>
        )}

        {/* ── Verify step ── */}
        {step === 'verify' && (
          <>
            <div className="text-center mb-6 mt-2">
              <div className="text-4xl mb-3">✉️</div>
              <h2 className="text-xl font-bold text-gray-800 mb-1">E-posta Doğrulama</h2>
              <p className="text-gray-400 text-sm">
                {form.email} adresine gönderilen 6 haneli kodu girin.
              </p>
            </div>

            <input type="text" inputMode="numeric"
              className={`${inpCls} text-center text-2xl tracking-[0.5em] font-mono mb-4`}
              placeholder="000000" maxLength={6} value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={e => e.key === 'Enter' && code.length === 6 && verifyCode()}
            />

            <button onClick={verifyCode} disabled={loading || code.length !== 6}
              className="w-full bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white font-semibold py-3 rounded-xl text-sm transition mb-3">
              {loading ? 'Doğrulanıyor...' : 'Doğrula & Giriş Yap'}
            </button>

            <div className="flex justify-between text-xs text-gray-400">
              <button onClick={() => { setStep('login'); setCode(''); setError(''); setInfo(''); }}
                className="hover:text-blue-700 transition">← Geri</button>
              <button onClick={resend} className="hover:text-blue-700 transition">Kod gelmedi? Tekrar gönder</button>
            </div>
          </>
        )}

        {/* ── Password reset: request a code ── */}
        {step === 'reset_request' && (
          <>
            <h2 className="text-xl font-bold text-gray-800 mb-1">Şifre Sıfırlama</h2>
            <p className="text-gray-400 text-sm mb-6">Hesabınızın e-posta adresini girin; size bir kod gönderelim.</p>

            <div className="mb-5">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">E-posta</label>
              <input type="email" className={inpCls} placeholder="email@adresiniz.com"
                value={form.email} maxLength={100}
                onChange={e => set('email', e.target.value)}
                onKeyDown={e => e.key === 'Enter' && resetRequest()} />
            </div>

            <button onClick={resetRequest} disabled={loading}
              className="w-full bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white font-semibold py-3 rounded-xl text-sm transition mb-3">
              {loading ? 'Gönderiliyor...' : 'Kod Gönder'}
            </button>
            <button onClick={() => { setStep('login'); setError(''); setInfo(''); }}
              className="w-full text-xs text-gray-400 hover:text-blue-700 transition">← Girişe dön</button>
          </>
        )}

        {/* ── Password reset: enter code + new password ── */}
        {step === 'reset_confirm' && (
          <>
            <div className="text-center mb-6 mt-2">
              <div className="text-4xl mb-3">🔑</div>
              <h2 className="text-xl font-bold text-gray-800 mb-1">Yeni Şifre Belirle</h2>
              <p className="text-gray-400 text-sm">
                {form.email} adresine gönderilen kodu ve yeni şifrenizi girin.
              </p>
            </div>

            <input type="text" inputMode="numeric"
              className={`${inpCls} text-center text-2xl tracking-[0.5em] font-mono mb-4`}
              placeholder="000000" maxLength={6} value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />

            <input type="password" autoComplete="new-password" className={`${inpCls} mb-4`}
              placeholder="Yeni şifre (min. 8 karakter)" maxLength={128}
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && code.length === 6 && resetConfirm()} />

            <button onClick={resetConfirm} disabled={loading || code.length !== 6}
              className="w-full bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white font-semibold py-3 rounded-xl text-sm transition mb-3">
              {loading ? 'Güncelleniyor...' : 'Şifreyi Güncelle'}
            </button>

            <div className="flex justify-between text-xs text-gray-400">
              <button onClick={() => { setStep('reset_request'); setError(''); setInfo(''); }}
                className="hover:text-blue-700 transition">← Geri</button>
              <button onClick={resetRequest} className="hover:text-blue-700 transition">Kod gelmedi? Tekrar gönder</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
